var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a;
import { action, IObservableArray, observable, set, makeObservable } from 'mobx';
import { default as api } from '../services/ImageExportConfigsApi';
import { DEFAULT_IMAGE_CODES } from '../framework/constants/exportConfig';
import { default as notificationStore } from './NotificationStore';
class ExportConfigsStore {
    constructor(notificationStore) {
        Object.defineProperty(this, "notificationStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "isFetching", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "exportConfigs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observable([])
        });
        Object.defineProperty(this, "loadExportConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.isFetching = true;
                return api.getExportConfigs()
                    .then((exportConfigs) => {
                    this.exportConfigs = observable(exportConfigs);
                    this.exportConfigs.forEach(this.setImages);
                })
                    .catch(this.notifyError)
                    .finally(() => {
                    this.isFetching = false;
                });
            }
        });
        Object.defineProperty(this, "createExportConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (exportConfig) => {
                exportConfig.forAll = true;
                if (!exportConfig.imageCodes) {
                    exportConfig.imageCodes = DEFAULT_IMAGE_CODES;
                }
                return api.createExportConfig(exportConfig)
                    .then(({ id }) => api.getExportConfig(id))
                    .catch(this.notifyError)
                    .then((exportConfig) => {
                    const length = this.exportConfigs.push(exportConfig);
                    this.setImages(this.exportConfigs[length - 1]);
                });
            }
        });
        Object.defineProperty(this, "updateExportConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (exportConfig) => {
                return api.updateExportConfig(exportConfig)
                    .catch(this.notifyError)
                    .then(() => {
                    const oldExportConfig = this.exportConfigs.find(({ id }) => {
                        return exportConfig.id === id;
                    });
                    set(oldExportConfig, exportConfig);
                });
            }
        });
        Object.defineProperty(this, "deleteExportConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (exportConfig) => {
                return api.deleteExportConfig(exportConfig)
                    .catch(this.notifyError)
                    .then(() => {
                    for (let i = 0; i < this.exportConfigs.length; i = i + 1) {
                        if (this.exportConfigs[i].id === exportConfig.id) {
                            this.exportConfigs.splice(i, 1);
                            return;
                        }
                    }
                });
            }
        });
        Object.defineProperty(this, "deleteBackgroundImage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (image) => {
                return api.deleteBackgroundImage(image)
                    .catch(this.notifyError)
                    .then(() => {
                    const { id, appointment } = image;
                    let images;
                    this.exportConfigs.some(({ advertisingImages, exportBackgroundImages }) => {
                        if (appointment === 'advertising') {
                            images = advertisingImages;
                        }
                        else {
                            images = exportBackgroundImages;
                        }
                        for (let i = 0; i < images.length; i = i + 1) {
                            if (images[i].id === id) {
                                images.splice(i, 1);
                                return true;
                            }
                        }
                        return false;
                    });
                });
            }
        });
        Object.defineProperty(this, "uploadBackgroundImage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (exportConfig, file, appointment) => {
                const { id, advertisingImages, exportBackgroundImages } = exportConfig;
                return api.uploadBackgroundImage(id, file, appointment)
                    .catch(this.notifyError)
                    .then(({ id, url }) => {
                    const image = {
                        id,
                        url,
                        appointment,
                        type: 'custom'
                    };
                    if (appointment === 'advertising') {
                        advertisingImages.push(image);
                    }
                    else {
                        exportBackgroundImages.push(image);
                    }
                    return image;
                });
            }
        });
        Object.defineProperty(this, "notifyError", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (reason) => {
                const { addNotification } = this.notificationStore;
                let message;
                if (reason !== undefined && Array.isArray(reason.errors)) {
                    const { errors } = reason;
                    if (errors.length > 0) {
                        const { detail, title } = errors[0];
                        if (detail !== undefined) {
                            message = detail;
                        }
                        else if (title !== undefined) {
                            message = title;
                        }
                    }
                }
                if (message === undefined) {
                    message = String(reason);
                }
                addNotification({
                    message,
                    level: 'error'
                });
                return Promise.reject(reason);
            }
        });
        // when images were not edited, export config don't have images objects, even if actually this config has images
        Object.defineProperty(this, "setImages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (exportConfig) => {
                const { wall, floor, watermark, auto_de_watermark, exportBackgroundImages } = exportConfig;
                exportConfig.wallImage = null;
                exportConfig.floorImage = null;
                exportConfig.watermarkImage = null;
                exportConfig.autoDeWatermarkImage = null;
                exportBackgroundImages.forEach((image) => {
                    const { url } = image;
                    if (url.endsWith(wall)) {
                        exportConfig.wallImage = image;
                    }
                    else if (url.endsWith(floor)) {
                        exportConfig.floorImage = image;
                    }
                    else if (url.endsWith(watermark)) {
                        exportConfig.watermarkImage = image;
                    }
                    else if (url.endsWith(auto_de_watermark)) {
                        exportConfig.autoDeWatermarkImage = image;
                    }
                });
            }
        });
        makeObservable(this);
        this.notificationStore = notificationStore;
    }
}
__decorate([
    observable,
    __metadata("design:type", Boolean)
], ExportConfigsStore.prototype, "isFetching", void 0);
__decorate([
    observable,
    __metadata("design:type", typeof (_a = typeof IObservableArray !== "undefined" && IObservableArray) === "function" ? _a : Object)
], ExportConfigsStore.prototype, "exportConfigs", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ExportConfigsStore.prototype, "loadExportConfig", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ExportConfigsStore.prototype, "createExportConfig", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ExportConfigsStore.prototype, "updateExportConfig", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ExportConfigsStore.prototype, "deleteExportConfig", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ExportConfigsStore.prototype, "deleteBackgroundImage", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ExportConfigsStore.prototype, "uploadBackgroundImage", void 0);
export default new ExportConfigsStore(notificationStore);
